import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Account } from "@common/types/domains/accounting/account";
import { NativeInvoiceLineRow } from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import { getAccounts } from "@api/accounting.api";
import { getAccountsQueryKey } from "@hooks/useAccounts";

type RemapAccount = Pick<Account, "id" | "code" | "isDisabled">;
type RemapLine = Pick<
    NativeInvoiceLineRow,
    "accrualAccountId" | "receivablesAccountId"
>;

interface RemapLineAccountsParams<TLine extends RemapLine> {
    lines: TLine[];
    fromAccounts: RemapAccount[];
    toAccounts: RemapAccount[];
}

export function remapLineAccountsToEntity<TLine extends RemapLine>({
    lines,
    fromAccounts,
    toAccounts,
}: RemapLineAccountsParams<TLine>): TLine[] {
    const activeToAccounts = toAccounts.filter(
        (account) => !account.isDisabled,
    );
    const toAccountIds = new Set(activeToAccounts.map((account) => account.id));
    const toIdByCode = new Map(
        activeToAccounts.map((account) => [account.code, account.id]),
    );
    const codeByFromId = new Map(
        fromAccounts.map((account) => [account.id, account.code]),
    );
    const remap = (accountId: string | null) => {
        if (accountId === null || toAccountIds.has(accountId)) {
            return accountId;
        }
        const code = codeByFromId.get(accountId);
        return code === undefined ? null : (toIdByCode.get(code) ?? null);
    };

    return lines.map((line) => ({
        ...line,
        accrualAccountId: remap(line.accrualAccountId),
        receivablesAccountId: remap(line.receivablesAccountId),
    }));
}

interface EntitySwitchAccountsParams {
    fromEntityId: number | null;
    toEntityId: number;
}

interface EntitySwitchAccounts {
    fromAccounts: Account[];
    toAccounts: Account[];
}

export function useEntitySwitchAccounts() {
    const queryClient = useQueryClient();

    return useCallback(
        async ({
            fromEntityId,
            toEntityId,
        }: EntitySwitchAccountsParams): Promise<EntitySwitchAccounts> => {
            const fetchAccounts = async (entityId: number | null) => {
                if (entityId === null) {
                    return [];
                }
                const response = await queryClient.fetchQuery({
                    queryKey: getAccountsQueryKey(entityId),
                    queryFn: () => getAccounts(entityId),
                });
                return response.accounts;
            };
            const [fromAccounts, toAccounts] = await Promise.all([
                fetchAccounts(fromEntityId),
                fetchAccounts(toEntityId),
            ]);
            return { fromAccounts, toAccounts };
        },
        [queryClient],
    );
}
